import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, ExternalLink, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Upload, X, Sparkles, Check, Store, Phone, Building2, CalendarDays, Loader2, Gauge, Zap, Fuel, Settings2, Calendar, Package, Globe, Download, AlertCircle, RefreshCw, MousePointerClick, BarChart3 } from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { STOCK_STATUS, STOCK_STATUS_CONFIG } from '../constants/vehicleStatus';
import { formatPrice, formatMileage, formatPowerFull } from '../utils/formatters';
import { getTransmissionLabel, getFuelTypeLabel, getBodyTypeLabel, getDriveTypeLabel, getEuroStandardLabel, getInteriorTypeLabel, getPaintTypeLabel } from '../constants/vehicleFields';
import { getAppointmentStatusConfig } from '../constants/marketplace';
import ContentCard from '../components/ContentCard';

const PLATFORMS = {
  '2ememain': {
    name: '2ememain.be',
    color: 'rgba(255, 107, 0, 0.5)',
    messageType: 'CARMIN_PUBLISH_2EMEMAIN',
    ackType: 'CARMIN_PUBLISH_2EMEMAIN_ACK',
    successTitle: 'Onglet 2ememain.be ouvert',
  },
  gocar: {
    name: 'GoCar.be',
    color: 'rgba(52, 152, 219, 0.5)',
    messageType: 'CARMIN_PUBLISH_GOCAR',
    ackType: 'CARMIN_PUBLISH_GOCAR_ACK',
    successTitle: 'Onglet GoCar.be ouvert',
  },
  autoscout24: {
    name: 'AutoScout24.be',
    color: 'rgba(255, 195, 0, 0.5)',
    messageType: 'CARMIN_PUBLISH_AUTOSCOUT24',
    ackType: 'CARMIN_PUBLISH_AUTOSCOUT24_ACK',
    successTitle: 'Onglet AutoScout24.be ouvert',
  },
  facebook: {
    name: 'Facebook',
    color: 'rgba(24, 119, 242, 0.5)',
    messageType: 'CARMIN_PUBLISH_FACEBOOK',
    ackType: 'CARMIN_PUBLISH_FACEBOOK_ACK',
    successTitle: 'Onglet Facebook ouvert',
  },
};

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [changingStatus, setChangingStatus] = useState(false);
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveName, setReserveName] = useState('');
  const [reservePhone, setReservePhone] = useState('');
  const [vehicleAppointments, setVehicleAppointments] = useState([]);
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [publishingPlatforms, setPublishingPlatforms] = useState({});
  const [publishSuccess, setPublishSuccess] = useState({});
  const [activeTab, setActiveTab] = useState('fiche');
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showHistSales, setShowHistSales] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-refresh while content is being generated in background
  // Polls until generated_content, generated_description, ad_title, and ad_description all exist
  useEffect(() => {
    if (vehicle && (!vehicle.generated_content || !vehicle.generated_description || !vehicle.ad_title || !vehicle.ad_description)) {
      const maxAttempts = 20; // Stop after ~60s (20 x 3s)
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(interval);
          return;
        }
        try {
          const response = await api.get(`/vehicles/${id}`);
          const data = response.data;
          setVehicle(data);
          if (data.generated_content && data.generated_description && data.ad_title && data.ad_description) {
            clearInterval(interval);
          }
        } catch (err) {
          // ignore
        }
      }, 3000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle?.generated_content, vehicle?.generated_description, vehicle?.ad_title, vehicle?.ad_description, id]);

  const loadVehicle = async () => {
    try {
      const response = await api.get(`/vehicles/${id}`);
      setVehicle(response.data);
      loadInsights();
    } catch (err) {
      setError('Véhicule non trouvé');
    } finally {
      setLoading(false);
    }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await api.get(`/vehicles/${id}/market-data`);
      setInsights(response.data);
    } catch (err) {
      // silently fail - insights are optional
    } finally {
      setInsightsLoading(false);
    }
  };

  const loadAppointments = async () => {
    try {
      const response = await api.get(`/vehicles/${id}/appointments`);
      setVehicleAppointments(response.data.appointments || []);
    } catch (err) {
      // silently fail - appointments are optional info
    }
  };

  useEffect(() => {
    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Detect Carmin Publisher extension
  useEffect(() => {
    const checkExtension = () => {
      const marker = document.documentElement.getAttribute('data-carmin-extension');
      setExtensionInstalled(marker === 'installed');
    };

    // Check immediately
    checkExtension();

    // Also listen for pong response (in case extension loads after React)
    const handleMessage = (event) => {
      if (event.data?.type === 'CARMIN_EXTENSION_PONG') {
        setExtensionInstalled(true);
      }
      Object.entries(PLATFORMS).forEach(([key, platform]) => {
        if (event.data?.type === platform.ackType) {
          setPublishingPlatforms(prev => ({ ...prev, [key]: false }));
          if (event.data.success) {
            setPublishSuccess(prev => ({ ...prev, [key]: true }));
            setTimeout(() => setPublishSuccess(prev => ({ ...prev, [key]: false })), 5000);
          }
        }
      });
    };

    window.addEventListener('message', handleMessage);

    // Ping the extension
    window.postMessage({ type: 'CARMIN_EXTENSION_PING' }, '*');

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const settingsRes = await api.get('/settings');
      return {
        _postal_code: settingsRes.data?.company_postal_code || null,
        _company_name: settingsRes.data?.company_name || null,
        _company_phone: settingsRes.data?.company_phone || null,
        _company_website: settingsRes.data?.company_website || null,
        _company_address: settingsRes.data?.company_address || null,
        _company_city: settingsRes.data?.company_city || null,
      };
    } catch (e) {
      return {};
    }
  };

  const handlePublish = async (platformKey, companyInfo = null) => {
    const platform = PLATFORMS[platformKey];
    setPublishingPlatforms(prev => ({ ...prev, [platformKey]: true }));
    setPublishSuccess(prev => ({ ...prev, [platformKey]: false }));

    if (!companyInfo) {
      companyInfo = await fetchCompanyInfo();
    }

    window.postMessage({
      type: platform.messageType,
      vehicle: { ...vehicle, ...companyInfo }
    }, '*');

    setTimeout(() => {
      setPublishingPlatforms(prev => ({ ...prev, [platformKey]: false }));
    }, 5000);
  };

  const handlePublishAll = async () => {
    const companyInfo = await fetchCompanyInfo();
    Object.keys(PLATFORMS).forEach(key => handlePublish(key, companyInfo));
  };

  const handleVehicleUpdate = (updatedVehicle) => {
    setVehicle(updatedVehicle);
  };

  const handleRegenerateAll = async () => {
    setRegeneratingAll(true);
    try {
      await api.post(`/vehicles/${id}/generate`);
      await api.post(`/vehicles/${id}/generate-description`);
      await api.post(`/vehicles/${id}/generate-ad-description`);
      const response = await api.get(`/vehicles/${id}`);
      setVehicle(response.data);
    } catch (e) {
      console.error('Regenerate all failed:', e);
    } finally {
      setRegeneratingAll(false);
    }
  };

  const isPlatformPublished = (key) => {
    const fromSource = key === 'autoscout24' && vehicle?.source_url?.includes('autoscout24');
    const entry = vehicle?.published_urls?.find(p => p.platform === key);
    return entry ? !entry.unpublished : fromSource;
  };

  const handleTogglePublished = async (platformKey) => {
    const fromSource = platformKey === 'autoscout24' && vehicle?.source_url?.includes('autoscout24');
    const entry = vehicle?.published_urls?.find(p => p.platform === platformKey);
    const isPublished = isPlatformPublished(platformKey);
    try {
      let response;
      if (isPublished) {
        if (fromSource && !entry) {
          response = await api.post(`/vehicles/${id}/published-url`, { platform: platformKey, unpublished: true });
        } else if (fromSource) {
          response = await api.post(`/vehicles/${id}/published-url`, { platform: platformKey, unpublished: true });
        } else {
          response = await api.delete(`/vehicles/${id}/published-url/${platformKey}`);
        }
      } else {
        if (entry?.unpublished) {
          response = await api.delete(`/vehicles/${id}/published-url/${platformKey}`);
        } else {
          response = await api.post(`/vehicles/${id}/published-url`, { platform: platformKey });
        }
      }
      setVehicle(response.data);
    } catch (err) {
      // ignore
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce véhicule ?')) return;
    setDeleting(true);
    try {
      await api.delete(`/vehicles/${id}`);
      navigate('/dashboard');
    } catch (err) {
      setError('Erreur lors de la suppression');
      setDeleting(false);
    }
  };

  const [marketplaceToast, setMarketplaceToast] = useState(null);

  const handleStockStatusChange = async (newStatus, extraData = {}) => {
    if (changingStatus) return;
    // If setting to reserved, show modal first (unless coming from modal submit)
    if (newStatus === STOCK_STATUS.RESERVED && !extraData._fromModal) {
      setReserveName(vehicle?.reserved_for_name || '');
      setReservePhone(vehicle?.reserved_for_phone || '');
      setShowReserveModal(true);
      return;
    }
    setChangingStatus(true);
    setMarketplaceToast(null);
    try {
      const response = await api.put(`/vehicles/${id}/stock-status`, {
        status: newStatus,
        ...extraData._fromModal ? {} : {},
        reserved_for_name: extraData.reserved_for_name || null,
        reserved_for_phone: extraData.reserved_for_phone || null,
      });
      setVehicle(response.data);

      // Show marketplace re-activation feedback
      if (newStatus === STOCK_STATUS.AVAILABLE && response.data.marketplace_listing_status === 'active') {
        setMarketplaceToast('reactivated');
      } else if (newStatus === STOCK_STATUS.AVAILABLE && !response.data.marketplace_listing_id) {
        setMarketplaceToast('suggest');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du changement de statut');
    } finally {
      setChangingStatus(false);
    }
  };

  const handleReserveSubmit = () => {
    setShowReserveModal(false);
    handleStockStatusChange(STOCK_STATUS.RESERVED, {
      _fromModal: true,
      reserved_for_name: reserveName || null,
      reserved_for_phone: reservePhone || null,
    });
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      const response = await api.post(`/vehicles/${id}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setVehicle(prev => ({ ...prev, images: response.data.images }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteImage = async (index) => {
    if (!window.confirm('Supprimer cette image ?')) return;
    try {
      const response = await api.delete(`/vehicles/${id}/images/${index}`);
      setVehicle(prev => ({ ...prev, images: response.data.images }));
      if (currentImageIndex >= response.data.images.length) {
        setCurrentImageIndex(Math.max(0, response.data.images.length - 1));
      }
    } catch (err) {
      setError('Erreur lors de la suppression de l\'image');
    }
  };

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !vehicle) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="text-carmin hover:text-carmin-light"
        >
          Retour au dashboard
        </button>
      </div>
    );
  }

  const images = vehicle?.images || [];
  const currentStockStatus = vehicle?.stock_status || STOCK_STATUS.AVAILABLE;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate('/dashboard')}
        className="inline-flex items-center text-secondary hover:text-primary mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Retour au dashboard
      </button>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 bg-dark-card rounded-lg p-1 border border-dark-border">
        {[
          { key: 'fiche', label: 'Fiche' },
          { key: 'contenu', label: 'Contenu' },
          { key: 'data', label: 'Data' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2.5 px-4 rounded-md text-sm font-semibold transition-all ${
              activeTab === tab.key
                ? 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30 shadow-sm'
                : 'text-muted hover:text-secondary hover:bg-dark-hover/50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'fiche' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left column - Images */}
        <div>
          <div className="bg-dark-card rounded-lg overflow-hidden">
            {/* Main image */}
            <div className="aspect-video bg-dark-hover relative">
              {images.length > 0 ? (
                <>
                  <img
                    src={images[currentImageIndex]}
                    alt={`${vehicle.make} ${vehicle.model}`}
                    className="w-full h-full object-cover"
                  />
                  {/* SOLD overlay */}
                  {currentStockStatus === STOCK_STATUS.SOLD && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <span className="bg-red-600 text-white text-2xl font-bold px-6 py-2 rounded-lg -rotate-12">
                        VENDU
                      </span>
                    </div>
                  )}
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setCurrentImageIndex(i => (i === 0 ? images.length - 1 : i - 1))}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={() => setCurrentImageIndex(i => (i === images.length - 1 ? 0 : i + 1))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1 rounded-full hover:bg-black/70"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-2 py-1 rounded">
                        {currentImageIndex + 1} / {images.length}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-secondary">
                  Pas d'image
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {images.length > 0 && (
              <div className="p-2 flex gap-2 overflow-x-auto">
                {images.map((img, index) => (
                  <div key={index} className="relative flex-shrink-0">
                    <button
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-16 h-16 rounded overflow-hidden ${
                        index === currentImageIndex ? 'ring-2 ring-carmin' : ''
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                    <button
                      onClick={() => handleDeleteImage(index)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                      title="Supprimer"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload button */}
            <div className="p-4 border-t border-dark-border">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={handleUploadClick}
                disabled={uploading}
                className="w-full inline-flex items-center justify-center px-4 py-2 border-2 border-dashed border-dark-border rounded-md text-secondary hover:border-carmin hover:text-carmin disabled:opacity-50"
              >
                {uploading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Upload...</span>
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 mr-2" />
                    Ajouter photos
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Vehicle info */}
          <div className="bg-dark-card rounded-lg p-6 mt-4">
            <h1 className="text-2xl font-bold text-primary">
              {vehicle.make} {vehicle.model}
            </h1>
            {vehicle.version && (
              <p className="text-muted">{vehicle.version}</p>
            )}
            <p className="text-2xl font-bold text-carmin mt-2">
              {formatPrice(vehicle.price)}
            </p>


            {vehicle.ai_tagline && (
              <p className="text-sm italic text-muted mt-2 border-l-2 border-carmin/50 pl-3">
                <Sparkles className="h-3 w-3 inline mr-1 text-carmin/50" />
                {vehicle.ai_tagline}
              </p>
            )}

            {/* Main specs - icon grid like mobile.de */}
            <div className="grid grid-cols-2 gap-4 mt-5">
              {vehicle.mileage && (
                <div className="flex items-center gap-3">
                  <Gauge className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">Kilométrage</p>
                    <p className="text-sm font-semibold text-primary">{formatMileage(vehicle.mileage)}</p>
                  </div>
                </div>
              )}
              {vehicle.power_kw && (
                <div className="flex items-center gap-3">
                  <Zap className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">Puissance</p>
                    <p className="text-sm font-semibold text-primary">{formatPowerFull(vehicle.power_kw)}</p>
                  </div>
                </div>
              )}
              {vehicle.fuel_type && (
                <div className="flex items-center gap-3">
                  <Fuel className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">Carburant</p>
                    <p className="text-sm font-semibold text-primary">{getFuelTypeLabel(vehicle.fuel_type)}</p>
                  </div>
                </div>
              )}
              {vehicle.transmission && (
                <div className="flex items-center gap-3">
                  <Settings2 className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">Transmission</p>
                    <p className="text-sm font-semibold text-primary">
                      {getTransmissionLabel(vehicle.transmission, 'full')}
                    </p>
                  </div>
                </div>
              )}
              {vehicle.first_registration && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">1ère immatriculation</p>
                    <p className="text-sm font-semibold text-primary">{vehicle.first_registration}</p>
                  </div>
                </div>
              )}
              {!vehicle.first_registration && vehicle.year && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-7 w-7 text-carmin flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted">Année</p>
                    <p className="text-sm font-semibold text-primary">{vehicle.year}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Technical data table - alternating rows like mobile.de */}
            {(() => {
              const rows = [
                vehicle.body_type && ['Carrosserie', getBodyTypeLabel(vehicle.body_type)],
                vehicle.color && ['Couleur', vehicle.color],
                vehicle.doors && ['Portes', vehicle.doors],
                vehicle.seats && ['Places', vehicle.seats],
                vehicle.engine_displacement_cc && ['Cylindrée', `${vehicle.engine_displacement_cc} cm³`],
                vehicle.cylinders && ['Cylindres', vehicle.cylinders],
                vehicle.gears && ['Vitesses', vehicle.gears],
                vehicle.drive_type && ['Transmission', getDriveTypeLabel(vehicle.drive_type)],
                vehicle.fuel_consumption && ['Consommation', `${vehicle.fuel_consumption} l/100km`],
                vehicle.co2_emissions && ['CO₂', `${vehicle.co2_emissions} g/km`],
                vehicle.euro_standard && ['Norme Euro', getEuroStandardLabel(vehicle.euro_standard)],
                vehicle.weight && ['Poids', vehicle.weight],
                vehicle.interior_type && ['Intérieur', getInteriorTypeLabel(vehicle.interior_type)],
                vehicle.interior_color && ['Couleur intérieure', vehicle.interior_color],
                vehicle.paint_type && ['Peinture', getPaintTypeLabel(vehicle.paint_type)],
                vehicle.previous_owners != null && ['Propriétaires précédents', vehicle.previous_owners],
                vehicle.warranty && ['Garantie', vehicle.warranty],
              ].filter(Boolean);
              if (rows.length === 0) return null;
              return (
                <div className="mt-5 pt-5 border-t border-dark-border">
                  <h3 className="text-lg font-bold text-primary mb-3">Données techniques</h3>
                  <div className="rounded-lg overflow-hidden">
                    {rows.map(([label, value], i) => (
                      <div key={label} className={`flex items-center justify-between px-4 py-2.5 ${i % 2 === 0 ? 'bg-dark-hover' : ''}`}>
                        <span className="text-sm text-secondary font-medium">{label}</span>
                        <span className="text-sm text-primary">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* History & condition flags */}
            {(() => {
              const flags = [
                vehicle.had_accident != null && ['Accidenté', vehicle.had_accident, true],
                vehicle.has_full_service_history != null && ['Carnet d\'entretien complet', vehicle.has_full_service_history, false],
                vehicle.non_smoking != null && ['Non-fumeur', vehicle.non_smoking, false],
                vehicle.has_particle_filter != null && ['Filtre à particules', vehicle.has_particle_filter, false],
                vehicle.tax_deductible != null && ['TVA déductible', vehicle.tax_deductible, false],
              ].filter(Boolean);
              if (flags.length === 0) return null;
              return (
                <div className="mt-5 pt-5 border-t border-dark-border">
                  <h3 className="text-lg font-bold text-primary mb-3">État du véhicule</h3>
                  <div className="rounded-lg overflow-hidden">
                    {flags.map(([label, value, invertColor], i) => (
                      <div key={label} className={`flex items-center justify-between px-4 py-2.5 ${i % 2 === 0 ? 'bg-dark-hover' : ''}`}>
                        <span className="text-sm text-secondary font-medium">{label}</span>
                        <span className={`text-sm font-medium ${
                          invertColor
                            ? (value ? 'text-red-400' : 'text-green-400')
                            : (value ? 'text-green-400' : 'text-muted')
                        }`}>
                          {value ? (invertColor ? 'Oui' : '✓') : (invertColor ? 'Non' : '—')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Equipment checklist - mobile.de style with checkmarks */}
            {vehicle.equipment && vehicle.equipment.length > 0 && (
              <div className="mt-5 pt-5 border-t border-dark-border">
                <h3 className="text-lg font-bold text-primary mb-3">Équipements</h3>
                <div className="rounded-lg overflow-hidden">
                  {vehicle.equipment.map((item, i) => (
                    <div key={i} className={`flex items-center justify-between px-4 py-2.5 ${i % 2 === 0 ? 'bg-dark-hover' : ''}`}>
                      <span className="text-sm text-label">{item}</span>
                      <Check className="h-4 w-4 text-muted" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {vehicle.source_url && (
              <a
                href={vehicle.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center mt-5 text-sm text-carmin hover:text-carmin-light"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                Voir sur AutoScout24
              </a>
            )}
          </div>

          {/* Autoconnect Data */}
          {vehicle.autoconnect_data && (
            <div className="bg-dark-card rounded-lg p-6 mt-4">
              <h3 className="text-lg font-semibold text-primary mb-4">Données Autoconnect</h3>
              {vehicle.autoconnect_data.legal_status_code === 'STL' && (
                <div className="bg-red-500/10 text-red-400 p-3 rounded-md mb-4 text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Véhicule signalé volé
                </div>
              )}
              {vehicle.autoconnect_data.status_code && vehicle.autoconnect_data.status_code !== 'NOR' && vehicle.autoconnect_data.legal_status_code !== 'STL' && (
                <div className="bg-yellow-500/10 text-yellow-400 p-3 rounded-md mb-4 text-sm">
                  Statut véhicule : {vehicle.autoconnect_data.status_code}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                {vehicle.autoconnect_data.catalog_price_excl_vat && (
                  <div><span className="text-muted">Prix catalogue HTVA</span><p className="text-primary font-medium">{Number(vehicle.autoconnect_data.catalog_price_excl_vat).toLocaleString('fr-BE')} €</p></div>
                )}
                {vehicle.autoconnect_data.version_name && (
                  <div><span className="text-muted">Version commerciale</span><p className="text-primary font-medium">{vehicle.autoconnect_data.version_name}</p></div>
                )}
                {vehicle.autoconnect_data.b2b_value && (
                  <div><span className="text-muted">Valeur B2B</span><p className="text-primary font-medium">{Number(vehicle.autoconnect_data.b2b_value).toLocaleString('fr-BE')} €</p></div>
                )}
                {vehicle.autoconnect_data.b2c_value && (
                  <div><span className="text-muted">Valeur B2C</span><p className="text-primary font-medium">{Number(vehicle.autoconnect_data.b2c_value).toLocaleString('fr-BE')} €</p></div>
                )}
                {vehicle.autoconnect_data.c2c_value && (
                  <div><span className="text-muted">Valeur C2C</span><p className="text-primary font-medium">{Number(vehicle.autoconnect_data.c2c_value).toLocaleString('fr-BE')} €</p></div>
                )}
                {vehicle.autoconnect_data.fiscal_hp && (
                  <div><span className="text-muted">CV fiscaux</span><p className="text-primary font-medium">{vehicle.autoconnect_data.fiscal_hp}</p></div>
                )}
                {vehicle.autoconnect_data.eco_score && (
                  <div><span className="text-muted">Ecoscore</span><p className="text-primary font-medium">{vehicle.autoconnect_data.eco_score}</p></div>
                )}
                {vehicle.autoconnect_data.road_tax_flanders && (
                  <div><span className="text-muted">Taxe circ. Flandre</span><p className="text-primary font-medium">{Number(vehicle.autoconnect_data.road_tax_flanders).toLocaleString('fr-BE')} €/an</p></div>
                )}
                {vehicle.autoconnect_data.road_tax_brussels && (
                  <div><span className="text-muted">Taxe circ. Bruxelles</span><p className="text-primary font-medium">{Number(vehicle.autoconnect_data.road_tax_brussels).toLocaleString('fr-BE')} €/an</p></div>
                )}
                {vehicle.autoconnect_data.road_tax_walloon && (
                  <div><span className="text-muted">Taxe circ. Wallonie</span><p className="text-primary font-medium">{Number(vehicle.autoconnect_data.road_tax_walloon).toLocaleString('fr-BE')} €/an</p></div>
                )}
                {vehicle.autoconnect_data.first_registration_date && (
                  <div><span className="text-muted">1ère immatriculation</span><p className="text-primary font-medium">{vehicle.autoconnect_data.first_registration_date}</p></div>
                )}
                {vehicle.autoconnect_data.technical_control_valid_date && (
                  <div><span className="text-muted">CT valide jusqu'au</span><p className="text-primary font-medium">{vehicle.autoconnect_data.technical_control_valid_date}</p></div>
                )}
                {vehicle.vin && (
                  <div><span className="text-muted">VIN</span><p className="text-primary font-mono text-xs">{vehicle.vin}</p></div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="bg-dark-card rounded-lg p-6 mt-4">
            <h3 className="text-sm font-medium text-muted mb-3">Actions</h3>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-red-500/30 text-red-400 rounded-md hover:bg-red-500/10 disabled:opacity-50 text-sm"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer ce véhicule
            </button>
          </div>
        </div>

        {/* Right column - Status, Publish & Actions */}
        <div>
          {/* Stock Status */}
          <div className="bg-dark-card rounded-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-primary">Statut du véhicule</h2>
              {vehicle.stock_status === STOCK_STATUS.SOLD && vehicle.sold_at && (
                <span className="text-xs text-secondary">
                  {new Date(vehicle.sold_at).toLocaleDateString('fr-BE')}
                </span>
              )}
            </div>
            <div className="relative">
              {(() => {
                const currentConfig = STOCK_STATUS_CONFIG[currentStockStatus] || STOCK_STATUS_CONFIG[STOCK_STATUS.AVAILABLE];
                const CurrentIcon = currentConfig.icon;
                return (
                  <>
                    <button
                      onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                      disabled={changingStatus}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-colors disabled:opacity-60 ${currentConfig.badge} border-dark-border hover:border-gray-600`}
                    >
                      <div className="flex items-center gap-2.5">
                        <CurrentIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">{currentConfig.label}</span>
                      </div>
                      {changingStatus ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ChevronDown className={`h-4 w-4 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                      )}
                    </button>
                    {showStatusDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-dark-card border border-dark-border rounded-lg overflow-hidden shadow-xl">
                        {Object.entries(STOCK_STATUS_CONFIG).map(([key, config]) => {
                          const Icon = config.icon;
                          const isActive = currentStockStatus === key;
                          return (
                            <button
                              key={key}
                              onClick={() => {
                                setShowStatusDropdown(false);
                                if (!isActive) handleStockStatusChange(key);
                              }}
                              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                                isActive
                                  ? `${config.badge} font-medium`
                                  : 'text-secondary hover:bg-dark-hover hover:text-primary'
                              }`}
                            >
                              <Icon className="h-4 w-4" />
                              {config.label}
                              {isActive && <Check className="h-3.5 w-3.5 ml-auto" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Reserved for info */}
            {currentStockStatus === STOCK_STATUS.RESERVED && (vehicle.reserved_for_name || vehicle.reserved_for_phone) && (
              <div className="mt-3 flex items-center gap-3 bg-blue-500/10 border border-blue-900/30 rounded-lg px-3 py-2.5">
                <Building2 className="h-4 w-4 text-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-300">
                    {vehicle.reserved_for_name || 'Un acheteur'}
                  </p>
                  {vehicle.reserved_for_phone && (
                    <a href={`tel:${vehicle.reserved_for_phone}`} className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-0.5">
                      <Phone className="h-3 w-3" />
                      {vehicle.reserved_for_phone}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Marketplace toasts */}
            {marketplaceToast === 'reactivated' && (
              <div className="mt-3 flex items-center gap-2 bg-green-500/10 border border-green-900/30 rounded-lg px-3 py-2.5">
                <Store className="h-4 w-4 text-green-400 flex-shrink-0" />
                <p className="text-xs text-green-400 flex-1">Annonce marketplace réactivée</p>
                <button onClick={() => setMarketplaceToast(null)} className="text-green-500 hover:text-green-400">
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            {marketplaceToast === 'suggest' && (
              <div className="mt-3 flex items-center gap-2 bg-indigo-500/10 border border-indigo-900/30 rounded-lg px-3 py-2.5">
                <Store className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                <p className="text-xs text-indigo-300 flex-1">Pas encore sur la marketplace</p>
                <a href="/marketplace" className="text-xs font-medium text-indigo-300 bg-indigo-500/20 hover:bg-indigo-500/30 px-2 py-1 rounded transition-colors">
                  Ajouter
                </a>
              </div>
            )}

            {/* Upcoming visits */}
            {vehicleAppointments.length > 0 && (
              <div className="mt-3 bg-indigo-500/10 border border-indigo-900/30 rounded-lg px-3 py-2.5">
                <p className="text-xs font-medium text-indigo-300 flex items-center gap-1.5 mb-2">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Visites prévues ({vehicleAppointments.length})
                </p>
                <div className="space-y-1.5">
                  {vehicleAppointments.map(appt => (
                    <div key={appt.id} className="flex items-center justify-between bg-dark-hover rounded px-2.5 py-1.5">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-xs text-primary font-medium truncate">{appt.buyer?.display_name || 'Acheteur'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-[11px] text-secondary">
                          {new Date(appt.date + 'T00:00:00').toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' })} à {appt.time}
                        </span>
                        {(() => {
                          const sc = getAppointmentStatusConfig(appt.status);
                          return (
                            <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${sc.bg} ${sc.text}`}>
                              {sc.label}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Purchase & cost tracking */}
          <div className="bg-dark-card rounded-lg p-6 mt-4">
            <h2 className="text-lg font-semibold text-primary mb-4">Achat & couts</h2>
            {vehicle.purchase_price && vehicle.price && (() => {
              const totalCosts = (vehicle.reconditioning_cost || 0) + (vehicle.mechanical_repairs || 0) + (vehicle.bodywork_cost || 0) + (vehicle.technical_inspection || 0) + (vehicle.transport_cost || 0);
              const netMargin = vehicle.price - vehicle.purchase_price - totalCosts;
              return (
                <div className={`flex items-center justify-between rounded-lg px-3 py-2 mb-4 ${netMargin > 0 ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                  <span className="text-xs text-muted">Marge nette</span>
                  <span className={`text-sm font-bold ${netMargin > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {netMargin.toLocaleString('fr-BE')} € {totalCosts > 0 && <span className="text-muted font-normal text-xs">(frais: {totalCosts.toLocaleString('fr-BE')} €)</span>}
                  </span>
                </div>
              );
            })()}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted mb-1">Prix d'achat</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      defaultValue={vehicle.purchase_price || ''}
                      key={`pp-${vehicle.purchase_price}`}
                      onBlur={async (e) => {
                        const val = e.target.value ? parseInt(e.target.value) : null;
                        if (val !== (vehicle.purchase_price || null)) {
                          try {
                            const res = await api.put(`/vehicles/${id}`, { purchase_price: val });
                            setVehicle(res.data);
                          } catch (err) { console.error(err); }
                        }
                      }}
                      className="w-full bg-dark-hover text-primary rounded-md px-2.5 py-1.5 text-sm border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                      placeholder="Non renseigne"
                      min="0"
                    />
                    <span className="text-muted text-xs">€</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Date d'entree</label>
                  <input
                    type="date"
                    defaultValue={vehicle.entry_date || (vehicle.created_at ? vehicle.created_at.slice(0, 10) : '')}
                    key={`ed-${vehicle.entry_date || vehicle.created_at}`}
                    onBlur={async (e) => {
                      const val = e.target.value || null;
                      const current = vehicle.entry_date || (vehicle.created_at ? vehicle.created_at.slice(0, 10) : null);
                      if (val !== current) {
                        try {
                          const res = await api.put(`/vehicles/${id}`, { entry_date: val });
                          setVehicle(res.data);
                        } catch (err) { console.error(err); }
                      }
                    }}
                    className="w-full bg-dark-hover text-primary rounded-md px-2.5 py-1.5 text-sm border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                  />
                  {(() => {
                    const entryStr = vehicle.entry_date || (vehicle.created_at ? vehicle.created_at.slice(0, 10) : null);
                    if (!entryStr) return null;
                    const days = Math.floor((Date.now() - new Date(entryStr).getTime()) / 86400000);
                    return <p className="text-xs text-muted mt-1">{days}j en stock</p>;
                  })()}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted mb-1">Preparation</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      defaultValue={vehicle.reconditioning_cost || ''}
                      key={`rc-${vehicle.reconditioning_cost}`}
                      onBlur={async (e) => {
                        const val = e.target.value ? parseFloat(e.target.value) : null;
                        if (val !== (vehicle.reconditioning_cost || null)) {
                          try {
                            const res = await api.put(`/vehicles/${id}`, { reconditioning_cost: val });
                            setVehicle(res.data);
                          } catch (err) { console.error(err); }
                        }
                      }}
                      className="w-full bg-dark-hover text-primary rounded-md px-2.5 py-1.5 text-sm border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                      placeholder="0"
                      min="0"
                    />
                    <span className="text-muted text-xs">€</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Reparations mecaniques</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      defaultValue={vehicle.mechanical_repairs || ''}
                      key={`mr-${vehicle.mechanical_repairs}`}
                      onBlur={async (e) => {
                        const val = e.target.value ? parseFloat(e.target.value) : null;
                        if (val !== (vehicle.mechanical_repairs || null)) {
                          try {
                            const res = await api.put(`/vehicles/${id}`, { mechanical_repairs: val });
                            setVehicle(res.data);
                          } catch (err) { console.error(err); }
                        }
                      }}
                      className="w-full bg-dark-hover text-primary rounded-md px-2.5 py-1.5 text-sm border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                      placeholder="0"
                      min="0"
                    />
                    <span className="text-muted text-xs">€</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted mb-1">Carrosserie</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      defaultValue={vehicle.bodywork_cost || ''}
                      key={`bw-${vehicle.bodywork_cost}`}
                      onBlur={async (e) => {
                        const val = e.target.value ? parseFloat(e.target.value) : null;
                        if (val !== (vehicle.bodywork_cost || null)) {
                          try {
                            const res = await api.put(`/vehicles/${id}`, { bodywork_cost: val });
                            setVehicle(res.data);
                          } catch (err) { console.error(err); }
                        }
                      }}
                      className="w-full bg-dark-hover text-primary rounded-md px-2.5 py-1.5 text-sm border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                      placeholder="0"
                      min="0"
                    />
                    <span className="text-muted text-xs">€</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-muted mb-1">Controle technique</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      defaultValue={vehicle.technical_inspection || ''}
                      key={`ti-${vehicle.technical_inspection}`}
                      onBlur={async (e) => {
                        const val = e.target.value ? parseFloat(e.target.value) : null;
                        if (val !== (vehicle.technical_inspection || null)) {
                          try {
                            const res = await api.put(`/vehicles/${id}`, { technical_inspection: val });
                            setVehicle(res.data);
                          } catch (err) { console.error(err); }
                        }
                      }}
                      className="w-full bg-dark-hover text-primary rounded-md px-2.5 py-1.5 text-sm border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                      placeholder="0"
                      min="0"
                    />
                    <span className="text-muted text-xs">€</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-muted mb-1">Transport</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      defaultValue={vehicle.transport_cost || ''}
                      key={`tc-${vehicle.transport_cost}`}
                      onBlur={async (e) => {
                        const val = e.target.value ? parseFloat(e.target.value) : null;
                        if (val !== (vehicle.transport_cost || null)) {
                          try {
                            const res = await api.put(`/vehicles/${id}`, { transport_cost: val });
                            setVehicle(res.data);
                          } catch (err) { console.error(err); }
                        }
                      }}
                      className="w-full bg-dark-hover text-primary rounded-md px-2.5 py-1.5 text-sm border border-dark-border focus:outline-none focus:ring-1 focus:ring-carmin"
                      placeholder="0"
                      min="0"
                    />
                    <span className="text-muted text-xs">€</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Publication */}
          {!extensionInstalled ? (
            <div className="bg-dark-card rounded-lg p-6 mt-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-carmin/20 flex items-center justify-center">
                  <Download className="h-5 w-5 text-carmin" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-primary">Carmin Publisher</h2>
                  <p className="text-xs text-secondary">Extension Chrome requise</p>
                </div>
              </div>

              <p className="text-sm text-secondary mb-5">
                Publiez vos véhicules en un clic sur 2ememain, GoCar, AutoScout24 et Facebook Marketplace.
              </p>

              <div className="space-y-3 mb-5">
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-carmin/20 text-carmin flex items-center justify-center text-xs font-bold">1</div>
                  <div>
                    <p className="text-sm text-primary font-medium">Téléchargez l'extension</p>
                    <a
                      href="/static/carmin-extension.zip"
                      download
                      className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1.5 bg-white text-gray-800 rounded-lg text-xs font-medium hover:bg-gray-100 transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Télécharger (.zip)
                    </a>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-carmin/20 text-carmin flex items-center justify-center text-xs font-bold">2</div>
                  <div>
                    <p className="text-sm text-primary font-medium">Installez dans Chrome</p>
                    <p className="text-xs text-muted mt-0.5">
                      Dézippez, puis <code className="bg-dark-hover px-1 py-0.5 rounded">chrome://extensions</code> → Mode développeur → Charger l'extension non empaquetée
                    </p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-carmin/20 text-carmin flex items-center justify-center text-xs font-bold">3</div>
                  <div>
                    <p className="text-sm text-primary font-medium">Rechargez cette page</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => window.location.reload()}
                className="w-full py-2.5 bg-carmin text-white rounded-lg text-sm font-semibold hover:bg-carmin-light transition-colors"
              >
                Recharger la page
              </button>
            </div>
          ) : (
            <div className="bg-dark-card rounded-lg p-6 mt-4">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-lg font-semibold text-primary">Publier une annonce</h2>
                <span className="inline-flex items-center text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">
                  <Check className="h-3 w-3 mr-1" />
                  Extension active
                </span>
              </div>
              <p className="text-xs text-secondary mb-4">
                Diffusez votre annonce sur les principales plateformes en un clic.
              </p>

              {/* Success messages */}
              {Object.entries(PLATFORMS).map(([key, platform]) => (
                publishSuccess[key] && (
                  <div key={key} className="flex items-center gap-2 bg-green-500/10 border border-green-900/30 rounded-lg px-3 py-2 mb-2 text-xs text-green-400">
                    <Check className="h-3.5 w-3.5 flex-shrink-0" />
                    <span>{platform.successTitle}</span>
                  </div>
                )
              ))}

              {/* Publish All */}
              {(() => {
                const totalPlatforms = Object.keys(PLATFORMS).length;
                const publishedCount = Object.keys(PLATFORMS).filter(k => isPlatformPublished(k)).length;
                const remaining = totalPlatforms - publishedCount;
                const isAnyPublishing = Object.values(publishingPlatforms).some(Boolean);
                if (remaining === 0) return null;
                return (
                  <button
                    onClick={handlePublishAll}
                    disabled={isAnyPublishing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-white/30 text-white rounded-lg font-bold hover:bg-white/10 disabled:opacity-50 transition-colors mb-3"
                  >
                    {isAnyPublishing ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Publication en cours...
                      </>
                    ) : (
                      <>
                        <MousePointerClick className="h-5 w-5" />
                        Publier partout ({remaining})
                      </>
                    )}
                  </button>
                );
              })()}

              {/* Help text */}
              <p className="text-xs text-muted mb-3">
                Publiez sur chaque plateforme, puis cochez pour marquer que c'est en ligne.
              </p>

              {/* Individual platforms */}
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(PLATFORMS).map(([key, platform]) => {
                  const isPublished = isPlatformPublished(key);
                  return (
                    <div key={key} className="relative">
                      <button
                        onClick={() => !isPublished && handlePublish(key)}
                        disabled={publishingPlatforms[key] || isPublished}
                        className={`w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-bold disabled:opacity-60 transition-all border-2 ${isPublished ? 'text-muted cursor-default' : 'text-white hover:bg-white/10 cursor-pointer'}`}
                        style={{ borderColor: isPublished ? 'rgba(255,255,255,0.15)' : (publishSuccess[key] ? '#22c55e' : platform.color) }}
                      >
                        {publishingPlatforms[key] ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : isPublished ? (
                          <Check className="h-3.5 w-3.5 text-muted" />
                        ) : publishSuccess[key] ? (
                          <Check className="h-3.5 w-3.5" />
                        ) : null}
                        {platform.name}
                      </button>
                      {/* Toggle published status */}
                      <button
                        onClick={() => handleTogglePublished(key)}
                        className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                          isPublished
                            ? 'bg-dark-hover border-green-500/60 text-green-500 hover:border-green-500'
                            : 'bg-dark-card border-dark-border text-muted hover:border-gray-500'
                        }`}
                        title={isPublished ? 'Marquer comme non publié' : 'Marquer comme publié'}
                      >
                        {isPublished && <Check className="h-3 w-3" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-md mt-4 text-sm">
              {error}
            </div>
          )}

        </div>
      </div>
      )}

      {activeTab === 'contenu' && (
        <div className="space-y-8 max-w-2xl">
          {/* Section: Annonces */}
          <div>
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Store className="h-4 w-4 text-muted" />
              Annonces
            </h3>
            <div className="space-y-3">
              <ContentCard
                vehicleId={id}
                label="Titre d'annonce"
                value={vehicle.ad_title}
                field="ad_title"
                maxChars={60}
                multiline={false}
                saveEndpoint={{ method: 'put', url: '/vehicles/{id}/ad-content', bodyKey: 'ad_title' }}
                generateEndpoint={{ url: '/vehicles/{id}/generate' }}
                onVehicleUpdate={handleVehicleUpdate}
              />
              <ContentCard
                vehicleId={id}
                label="Description d'annonce"
                value={vehicle.ad_description}
                field="ad_description"
                multiline={true}
                saveEndpoint={{ method: 'put', url: '/vehicles/{id}/ad-content', bodyKey: 'ad_description' }}
                generateEndpoint={{ url: '/vehicles/{id}/generate-ad-description' }}
                onVehicleUpdate={handleVehicleUpdate}
              />
            </div>
          </div>

          {/* Section: Réseaux sociaux */}
          <div>
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted" />
              Réseaux sociaux
            </h3>
            <div className="space-y-3">
              <ContentCard
                vehicleId={id}
                label="Caption social"
                value={vehicle.generated_content?.caption}
                field="caption"
                multiline={true}
                saveEndpoint={{ method: 'put', url: '/vehicles/{id}/content', bodyKey: 'caption' }}
                generateEndpoint={{ url: '/vehicles/{id}/generate' }}
                onVehicleUpdate={handleVehicleUpdate}
                charWarning={125}
                charWarningLabel="Les 125 premiers caractères sont visibles sur Instagram"
                hashtags={vehicle.generated_content?.hashtags}
              />
              {vehicle.stock_status === 'sold' && (
                <ContentCard
                  vehicleId={id}
                  label="Caption VENDU"
                  value={vehicle.sold_content?.caption}
                  field="sold_caption"
                  multiline={true}
                  saveEndpoint={{ method: 'put', url: '/vehicles/{id}/sold-content', bodyKey: 'caption' }}
                  generateEndpoint={{ url: '/vehicles/{id}/generate-sold' }}
                  onVehicleUpdate={handleVehicleUpdate}
                  hashtags={vehicle.sold_content?.hashtags}
                />
              )}
              {vehicle.price_reduced && (
                <ContentCard
                  vehicleId={id}
                  label="Caption PROMO"
                  value={vehicle.promo_content?.caption}
                  field="promo_caption"
                  multiline={true}
                  saveEndpoint={{ method: 'put', url: '/vehicles/{id}/promo-content', bodyKey: 'caption' }}
                  generateEndpoint={{ url: '/vehicles/{id}/generate-promo' }}
                  onVehicleUpdate={handleVehicleUpdate}
                  hashtags={vehicle.promo_content?.hashtags}
                />
              )}
            </div>
          </div>

          {/* Section: Vitrine web */}
          <div>
            <h3 className="text-white font-medium mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-muted" />
              Vitrine web
            </h3>
            <div className="space-y-3">
              <ContentCard
                vehicleId={id}
                label="Tagline (accroche courte)"
                value={vehicle.ai_tagline}
                field="ai_tagline"
                maxChars={120}
                multiline={false}
                saveEndpoint={{ method: 'put', url: '/vehicles/{id}/tagline', bodyKey: 'ai_tagline' }}
                generateEndpoint={{ url: '/vehicles/{id}/generate' }}
                onVehicleUpdate={handleVehicleUpdate}
              />
              <ContentCard
                vehicleId={id}
                label="Description vitrine"
                value={vehicle.generated_description}
                field="generated_description"
                multiline={true}
                saveEndpoint={{ method: 'put', url: '/vehicles/{id}/description', bodyKey: 'generated_description' }}
                generateEndpoint={{ url: '/vehicles/{id}/generate-description' }}
                onVehicleUpdate={handleVehicleUpdate}
              />
            </div>
          </div>

          {/* Regenerate All button */}
          <div className="pt-4 border-t border-dark-border">
            <button
              onClick={handleRegenerateAll}
              disabled={regeneratingAll}
              className="flex items-center gap-2 px-4 py-2.5 bg-accent-purple text-white rounded-lg hover:bg-accent-purple/80 disabled:opacity-50 transition-colors text-sm"
            >
              {regeneratingAll ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Tout régénérer
            </button>
          </div>
        </div>
      )}

      {activeTab === 'data' && (
        <div className="space-y-4">
          {insightsLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted" />
              <span className="ml-3 text-muted">Analyse du marché en cours...</span>
            </div>
          ) : insights?.error ? (
            <div className="bg-dark-card rounded-lg p-8 text-center">
              <AlertCircle className="h-8 w-8 text-muted mx-auto mb-3" />
              <p className="text-muted">{insights.message}</p>
            </div>
          ) : (insights?.live_market?.count > 0 || insights?.historical?.count > 0) ? (() => {
            const hasLive = insights.live_market?.count > 0;
            const histCount = insights.historical?.count || 0;
            const liveCount = insights.live_market?.count || 0;
            const source = hasLive ? insights.live_market : insights.historical;
            const sourceLabel = hasLive ? 'live' : 'historical';
            const median = source.price_median;
            const p25 = source.p25 || null;
            const p75 = source.p75 || null;
            const totalSample = Math.max(histCount, liveCount);
            const velocity = insights.historical?.velocity_distribution || [];

            // Price position from backend
            const pp = insights.price_position;
            const diffAmt = pp?.vs_median_amount || 0;
            const diffPct = pp?.vs_median_pct || 0;
            const positioning = pp?.positioning;

            const getPositioningStyle = (pos) => {
              if (pos === 'very_competitive') return { label: 'Très compétitif', cls: 'bg-green-500/10 text-green-400 border-green-500/30' };
              if (pos === 'competitive')      return { label: 'Compétitif',      cls: 'bg-green-500/10 text-green-300 border-green-500/30' };
              if (pos === 'market_price')     return { label: 'Dans le marché',  cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' };
              if (pos === 'above_market')     return { label: 'Au-dessus',       cls: 'bg-orange-500/10 text-orange-400 border-orange-500/30' };
              return                            { label: 'Trop cher',      cls: 'bg-red-500/10 text-red-400 border-red-500/30' };
            };
            const pos = pp ? getPositioningStyle(positioning) : null;

            // Situation verdict from backend
            const sv = insights.situation_verdict || {};
            const rotRange = sv.rotation_range;
            const daysInStock = sv.days_in_stock;
            const avgDays = sv.avg_days_to_sell || insights.historical?.avg_days_to_sell;

            const getSituationStyle = (status) => {
              if (status === 'on_track')        return { cls: 'bg-green-500/10 text-green-400 border-green-500/30', icon: '\u2705' };
              if (status === 'watch')           return { cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30', icon: '\uD83D\uDC40' };
              if (status === 'action_required') return { cls: 'bg-orange-500/10 text-orange-400 border-orange-500/30', icon: '\u26A0\uFE0F' };
              return                              { cls: 'bg-red-500/10 text-red-400 border-red-500/30', icon: '\uD83D\uDEA8' };
            };
            const sit = getSituationStyle(sv.status);

            return (
              <>
                {/* SECTION 1 — EN-TÊTE CONTEXTUEL */}
                <div className="bg-dark-card rounded-lg p-5 border border-dark-border">
                  <div className="flex items-baseline justify-between flex-wrap gap-2">
                    <h2 className="text-primary font-bold text-lg">
                      {vehicle.make} {vehicle.model}
                    </h2>
                    <span className="text-blue-400 font-bold text-lg">{vehicle.price?.toLocaleString('fr-BE')} €</span>
                  </div>
                  <div className="text-secondary text-sm mt-1">
                    {vehicle.year} — {vehicle.mileage ? Number(vehicle.mileage).toLocaleString('fr-BE') : ''} km — {vehicle.fuel_type || ''}
                  </div>

                  <div className="text-muted text-xs mt-3">
                    {!hasLive ? (
                      <>
                        <span className="text-yellow-400 font-medium">Aucune annonce live similaire en Belgique</span>
                        <span> · basé sur {histCount} ventes passées</span>
                        <div className="mt-1.5 flex items-start gap-1.5 text-yellow-400/80 bg-yellow-500/5 border border-yellow-500/10 rounded-lg px-3 py-2">
                          <span className="shrink-0">💡</span>
                          <span>Aucun concurrent en ligne actuellement. Cela peut signifier une faible demande sur ce segment ou une fenêtre d'opportunité.</span>
                        </div>
                      </>
                    ) : (
                      <span>{liveCount} annonce{liveCount > 1 ? 's' : ''} active{liveCount > 1 ? 's' : ''} · {histCount > 0 ? `${histCount} ventes passées` : 'pas de données historiques'}</span>
                    )}
                  </div>

                  {insights.sample_warning === 'low_sample' && (
                    <div className="mt-2 flex items-center gap-1.5 text-xs text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-1.5">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      <span>Échantillon limité ({totalSample} véhicule{totalSample > 1 ? 's' : ''}) — les estimations peuvent être moins fiables</span>
                    </div>
                  )}
                  {insights.data_confidence && insights.data_confidence.level !== 'high' && (
                    <div className={`mt-2 flex items-center gap-1.5 text-xs rounded-lg px-3 py-1.5 ${
                      insights.data_confidence.level === 'low' ? 'text-orange-400 bg-orange-500/10 border border-orange-500/20' : 'text-yellow-400 bg-yellow-500/10 border border-yellow-500/20'
                    }`}>
                      <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      <span>{insights.data_confidence.message || `Confiance ${insights.data_confidence.level} — ${insights.data_confidence.reason || 'données limitées'}`}</span>
                    </div>
                  )}
                </div>

                {/* SECTION 2 — DEUX VERDICTS */}
                <div className="grid grid-cols-2 gap-4">
                  {pos && (
                    <div className={`bg-dark-card rounded-lg p-5 border ${pos.cls.split(' ').find(c => c.startsWith('border-'))}`}>
                      <div className="text-[11px] text-muted uppercase tracking-wider mb-2">Verdict prix</div>
                      <span className={`${pos.cls} border rounded-full px-3.5 py-1 text-sm font-bold inline-block`}>{pos.label}</span>
                      <div className="mt-2 text-sm text-secondary">
                        {diffAmt < 0
                          ? <><span className="text-green-400 font-semibold">{Math.abs(diffAmt).toLocaleString('fr-BE')} € sous</span> la médiane ({Math.abs(diffPct)}%)</>
                          : diffAmt > 0
                            ? <><span className="text-orange-400 font-semibold">+{diffAmt.toLocaleString('fr-BE')} € au-dessus</span> (+{diffPct}%)</>
                            : <span className="text-yellow-400 font-semibold">Au prix médian</span>
                        }
                      </div>
                      <div className="text-[11px] text-muted mt-1.5">
                        vs médiane {pp.source === 'live' ? 'live' : 'historique'} ({pp.median?.toLocaleString('fr-BE')} €)
                        {pp.source === 'historical' && <span className="text-yellow-400"> · pas de concurrent live</span>}
                      </div>
                    </div>
                  )}

                  <div className={`bg-dark-card rounded-lg p-5 border ${sit.cls.split(' ').find(c => c.startsWith('border-'))}`}>
                    <div className="text-[11px] text-muted uppercase tracking-wider mb-2">Situation</div>
                    <span className={`${sit.cls} border rounded-full px-3.5 py-1 text-sm font-bold inline-block`}>{sit.icon} {sv.label || 'En bonne voie'}</span>
                    <div className="mt-2 text-sm text-secondary">
                      {daysInStock != null ? (
                        <>En stock depuis <span className="text-primary font-semibold">{daysInStock}j</span></>
                      ) : 'Durée en stock inconnue'}
                    </div>
                    {rotRange && (
                      <div className="text-[11px] text-muted mt-1.5">
                        Rotation du segment : {rotRange[0]}–{rotRange[1]} jours {avgDays ? `(moy. ~${Math.round(avgDays)}j)` : ''}
                      </div>
                    )}
                    {insights.segment_trend?.trend_direction && (
                      <div className={`text-[11px] mt-1.5 ${
                        insights.segment_trend.trend_direction === 'declining' ? 'text-red-400' :
                        insights.segment_trend.trend_direction === 'rising' ? 'text-green-400' : 'text-muted'
                      }`}>
                        {insights.segment_trend.trend_direction === 'declining' && `Segment en baisse (${insights.segment_trend.price_trend_30d_pct}% sur 30j)`}
                        {insights.segment_trend.trend_direction === 'rising' && `Segment en hausse (+${insights.segment_trend.price_trend_30d_pct}% sur 30j)`}
                        {insights.segment_trend.trend_direction === 'stable' && 'Segment stable'}
                      </div>
                    )}
                  </div>
                </div>

                {/* SECTION 3 — RECOMMANDATIONS */}
                {sv.status !== 'on_track' && sv.diagnostic && (
                  <div className="bg-dark-card rounded-lg p-5 border border-orange-500/20">
                    <div className="font-bold text-primary text-base mb-3">Recommandations</div>
                    <div className="bg-dark-bg rounded-lg p-3.5 border border-dark-border text-sm text-secondary leading-relaxed mb-3">
                      {sv.diagnostic}
                    </div>
                    {sv.actions?.length > 0 && (
                      <>
                        <div className="text-xs text-muted mb-2 font-medium">Actions à vérifier :</div>
                        <div className="space-y-1.5">
                          {sv.actions.map((action, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-secondary">
                              <span className="text-muted mt-0.5 shrink-0">•</span>
                              <span>{action}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* SECTION 4 — CONCURRENCE LIVE */}
                <div className="bg-dark-card rounded-lg p-5 border border-dark-border">
                  <div className="flex items-center gap-2 mb-4">
                    <Globe className="h-4 w-4 text-blue-400" />
                    <span className="font-bold text-primary text-base">Concurrence en ligne</span>
                    <span className="ml-auto text-[11px] text-green-400 bg-green-500/10 border border-green-500/20 rounded-full px-2.5 py-0.5">● Live</span>
                  </div>

                  {liveCount === 0 ? (
                    <div className="bg-dark-bg rounded-lg p-4 text-center">
                      <div className="text-2xl mb-2">🔍</div>
                      <div className="text-sm text-primary font-medium">Aucun concurrent en ligne</div>
                      <div className="text-xs text-muted mt-1">Aucune annonce similaire trouvée en Belgique pour ce modèle, cette année et ce kilométrage.</div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-dark-bg rounded-lg p-3.5">
                          <div className="text-[11px] text-muted uppercase tracking-wide mb-1">En Belgique</div>
                          <div className="text-xl font-bold text-primary">{liveCount}</div>
                          <div className="text-xs text-muted mt-0.5">annonces similaires</div>
                        </div>
                        <div className="bg-dark-bg rounded-lg p-3.5 border border-blue-500/20">
                          <div className="text-[11px] text-muted uppercase tracking-wide mb-1">Dans {insights.live_market.nearby_radius_km || 50} km</div>
                          <div className="text-xl font-bold text-blue-400">{insights.live_market.nearby_count ?? '—'}</div>
                          <div className="text-xs text-muted mt-0.5">concurrents directs</div>
                        </div>
                      </div>
                      {insights.competitors?.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-dark-border">
                          <p className="text-[11px] text-muted mb-2">Annonces concurrentes ({insights.competitors.length})</p>
                          <div className="max-h-48 overflow-y-auto space-y-0.5">
                            {insights.competitors.map((c, i) => (
                              <a key={i} href={c.url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center justify-between text-xs p-2 rounded-md hover:bg-dark-hover transition-colors group">
                                <span className="text-secondary">
                                  <span className="text-primary font-semibold">{Number(c.price).toLocaleString('fr-BE')} €</span> · {c.year} · {Number(c.mileage).toLocaleString('fr-BE')} km · {c.location?.replace('BE-', '')}
                                  {c.days_online != null && <span className="text-muted"> · {c.days_online}j en ligne</span>}
                                  {c.price_changes_count > 0 && <span className="text-orange-400"> · {c.price_changes_count} baisse{c.price_changes_count > 1 ? 's' : ''}</span>}
                                </span>
                                <ExternalLink className="h-3 w-3 text-muted opacity-0 group-hover:opacity-100 shrink-0 ml-2" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* SECTION 5 — PRIX & ROTATION (Option C — vue simple) */}
                <div className="bg-dark-card rounded-lg p-5 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Gauge className="h-4 w-4 text-blue-400" />
                    <span className="font-bold text-primary text-base">Prix & Rotation</span>
                    {insights.historical_sales?.length > 0 ? (
                      <button
                        onClick={() => setShowHistSales(!showHistSales)}
                        className={`ml-auto flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-full cursor-pointer transition-colors ${sourceLabel === 'live' ? 'text-green-400 bg-green-500/10 border border-green-500/20 hover:bg-green-500/20' : 'text-blue-400 bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/20'}`}
                      >
                        {sourceLabel === 'live' ? (
                          <><Globe className="h-3 w-3" /> {liveCount} annonces live</>
                        ) : (
                          <><BarChart3 className="h-3 w-3" /> {histCount} ventes historiques</>
                        )}
                        {showHistSales ? <ChevronUp className="h-3 w-3 ml-0.5" /> : <ChevronDown className="h-3 w-3 ml-0.5" />}
                      </button>
                    ) : (
                      <span className={`ml-auto flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-full ${sourceLabel === 'live' ? 'text-green-400 bg-green-500/10 border border-green-500/20' : 'text-blue-400 bg-blue-500/10 border border-blue-500/20'}`}>
                        {sourceLabel === 'live' ? (
                          <><Globe className="h-3 w-3" /> {liveCount} annonces live</>
                        ) : (
                          <><BarChart3 className="h-3 w-3" /> {histCount} ventes historiques</>
                        )}
                      </span>
                    )}
                  </div>

                  {/* Matching criteria */}
                  {insights.matching && (
                    <div className="text-muted text-[11px] mb-4">
                      même modèle · {insights.matching.fuel_type || 'même carburant'} · {insights.matching.year_range[0] === insights.matching.year_range[1] ? `année ${insights.matching.year_range[0]}` : `${insights.matching.year_range[0]}–${insights.matching.year_range[1]}`} · {Number(insights.matching.mileage_range[0]).toLocaleString('fr-BE')}–{Number(insights.matching.mileage_range[1]).toLocaleString('fr-BE')} km
                      {insights.matching.mode === 'relaxed' && <span className="text-yellow-400 ml-1">(critères élargis)</span>}
                    </div>
                  )}

                  {/* Historical sales panel (expandable) */}
                  {showHistSales && insights.historical_sales?.length > 0 && (
                    <div className="bg-dark-bg rounded-lg border border-blue-500/10 mb-4 overflow-hidden">
                      <div className="px-3 py-2 border-b border-dark-border flex justify-between items-center">
                        <span className="text-xs text-muted font-medium">Véhicules similaires vendus ({insights.historical_sales.length})</span>
                        <div className="flex gap-4 text-[10px] text-muted">
                          <span className="w-20 text-right">Prix final</span>
                          <span className="w-12 text-right">Négo</span>
                          <span className="w-14 text-right">Vendu en</span>
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        {insights.historical_sales.map((s, i) => {
                          const hasListedPrice = s.listed_price != null;
                          const drops = s.price_drops || [];
                          const hasDrop = drops.length > 0 || (hasListedPrice && s.sold_price && s.listed_price !== s.sold_price);
                          const dropPct = hasListedPrice && hasDrop ? Math.round(((s.listed_price - s.sold_price) / s.listed_price) * 100) : 0;

                          // Build rich tooltip
                          let tooltipLines;
                          if (!hasListedPrice) {
                            tooltipLines = [`Vendu à ${Number(s.sold_price).toLocaleString('fr-BE')} € (prix initial inconnu)`];
                          } else if (drops.length > 0) {
                            tooltipLines = [`${Number(s.listed_price).toLocaleString('fr-BE')} €`];
                            drops.forEach(d => {
                              const amt = d.from - d.to;
                              tooltipLines.push(`→ −${Number(amt).toLocaleString('fr-BE')} € au jour ${d.day}`);
                            });
                            tooltipLines.push(`→ vendu ${Number(s.sold_price).toLocaleString('fr-BE')} € en ${s.days_to_sell}j`);
                          } else if (hasDrop) {
                            tooltipLines = [`${Number(s.listed_price).toLocaleString('fr-BE')} € → vendu ${Number(s.sold_price).toLocaleString('fr-BE')} € en ${s.days_to_sell}j`];
                          } else {
                            tooltipLines = [`Vendu au prix affiché : ${Number(s.sold_price).toLocaleString('fr-BE')} € (aucune baisse)`];
                          }

                          return (
                            <div key={i} className="group relative flex items-center justify-between px-3 py-2 text-xs border-b border-dark-border/50 last:border-0 hover:bg-dark-hover/50">
                              <div className="flex-1 min-w-0">
                                <span className="text-secondary">
                                  {s.make} {s.model} · {s.year} · {Number(s.mileage).toLocaleString('fr-BE')} km
                                  {s.location && <span className="text-muted"> · {s.location}</span>}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 shrink-0">
                                <span className="w-20 text-right font-semibold text-primary">{Number(s.sold_price).toLocaleString('fr-BE')} €</span>
                                <span className="w-12 text-right">
                                  {!hasListedPrice ? (
                                    <span className="text-muted cursor-help">—</span>
                                  ) : dropPct > 0 ? (
                                    <span className="text-orange-400 cursor-help border-b border-dotted border-orange-400/40">-{dropPct}%{drops.length > 1 ? ` (${drops.length}x)` : ''}</span>
                                  ) : (
                                    <span className="text-green-400/60 cursor-help border-b border-dotted border-green-400/20">=</span>
                                  )}
                                </span>
                                <span className="w-14 text-right font-medium text-blue-400">{s.days_to_sell}j</span>
                              </div>
                              {/* Tooltip on hover */}
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-3 py-1.5 bg-dark-card border border-dark-border rounded-lg shadow-lg text-[11px] text-secondary opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-10 whitespace-pre-line">
                                {tooltipLines.join('\n')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Two cards: your price vs market */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-dark-bg rounded-lg p-4 text-center">
                      <div className="text-[11px] text-muted uppercase tracking-wide mb-1">Votre prix</div>
                      <div className="text-2xl font-extrabold text-primary">{vehicle.price?.toLocaleString('fr-BE')} €</div>
                      {diffAmt !== 0 && (
                        <div className={`text-xs mt-1.5 font-medium ${diffAmt < 0 ? 'text-green-400' : 'text-orange-400'}`}>
                          {diffAmt < 0 ? `${Math.abs(diffPct)}% sous la médiane` : `+${Math.abs(diffPct)}% au-dessus`}
                        </div>
                      )}
                    </div>
                    <div className="bg-dark-bg rounded-lg p-4 text-center">
                      <div className="text-[11px] text-muted uppercase tracking-wide mb-1">Médiane du marché</div>
                      <div className="text-2xl font-extrabold text-secondary">{median?.toLocaleString('fr-BE')} €</div>
                      <div className="text-xs text-muted mt-1.5">sur {totalSample} véhicule{totalSample > 1 ? 's' : ''} similaire{totalSample > 1 ? 's' : ''}</div>
                    </div>
                  </div>

                  {/* Position bar */}
                  {median && (() => {
                    const rangeMin = source.p10 || source.price_min || Math.round(median * 0.7);
                    const rangeMax = source.p90 || source.price_max || Math.round(median * 1.3);
                    const pct = (v) => Math.max(0, Math.min(100, ((v - rangeMin) / (rangeMax - rangeMin)) * 100));
                    const yourPct = pct(vehicle.price);
                    return (
                      <div className="relative my-6 mx-2">
                        <div className="h-3 rounded-md" style={{ background: 'linear-gradient(to right, #22c55e 0%, #86efac 25%, #fbbf24 50%, #f97316 75%, #ef4444 100%)' }}>
                          {[p25, median, p75].filter(Boolean).map((v, i) => (
                            <div key={i} className="absolute top-0 w-px h-3 bg-black/40" style={{ left: `${pct(v)}%` }} />
                          ))}
                        </div>
                        <div className="absolute" style={{ left: `${yourPct}%`, top: '-8px', transform: 'translateX(-50%)' }}>
                          <div className="w-7 h-7 rounded-full bg-blue-500 shadow-lg" style={{ border: '3px solid white' }} />
                          <div className="text-[10px] text-blue-400 font-semibold text-center mt-1 whitespace-nowrap">Votre prix</div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Percentiles */}
                  <div className="flex justify-between mt-10 mb-4">
                    {[
                      ['Min', source.price_min],
                      ['P25', p25],
                      ['Médiane', median],
                      ['P75', p75],
                      ['Max', source.price_max],
                    ].filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="text-center">
                        <div className="text-sm font-semibold text-primary">{v?.toLocaleString('fr-BE')} €</div>
                        <div className={`text-[11px] ${k === 'Médiane' ? 'text-yellow-400 font-semibold' : 'text-muted'}`}>{k}</div>
                      </div>
                    ))}
                  </div>

                  {/* Rotation range */}
                  {rotRange && (
                    <div className="bg-green-500/5 border border-green-500/15 rounded-lg p-4 text-center mt-4">
                      <div className="text-[11px] text-muted uppercase tracking-wide mb-1">Rotation estimée du segment</div>
                      <div className="text-2xl font-bold text-green-400">{rotRange[0]} – {rotRange[1]} jours</div>
                      {avgDays && <div className="text-xs text-muted mt-1">moyenne ~{Math.round(avgDays)} jours</div>}
                      {(() => {
                        let cumul14 = 0;
                        for (const v of velocity) {
                          cumul14 += v.pct;
                          if (v.range === '7-14j') break;
                        }
                        return cumul14 > 0 ? (
                          <div className="text-xs text-muted mt-1">{Math.round(cumul14)}% se sont vendus en moins de 14 jours</div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>

                {/* SECTION 6 — VÉLOCITÉ */}
                {velocity.length > 0 && (
                  <div className="bg-dark-card rounded-lg p-5 border border-dark-border">
                    <div className="font-bold text-primary text-base mb-3">Délai de vente observé</div>
                    <div className="flex justify-between text-[11px] text-muted mb-2.5">
                      <span>Tranche</span>
                      <div className="flex gap-7 pr-1"><span>Part</span><span>Cumul</span></div>
                    </div>
                    {(() => {
                      let c = 0;
                      return velocity.map((v, i) => {
                        c += v.pct;
                        return (
                          <div key={v.range} className="flex items-center gap-2.5 mb-1.5">
                            <span className="w-12 text-xs shrink-0 text-muted">{v.range}</span>
                            <div className="flex-1 bg-dark-border rounded-sm overflow-hidden h-2">
                              <div className="h-full rounded-sm bg-blue-600/60" style={{ width: `${v.pct}%` }} />
                            </div>
                            <span className="w-10 text-xs text-right text-muted">{v.pct}%</span>
                            <span className="w-10 text-[11px] text-right text-muted">{Math.round(c)}%</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                {/* SECTION — ESTIMATION ACHAT/REVENTE (dealer pricing) */}
                {insights.dealer_pricing?.pricing && (() => {
                  const dp = insights.dealer_pricing.pricing;
                  const acq = dp.acquisition || {};
                  const resale = dp.resale || {};
                  const ctx = dp.market_context || {};
                  return (
                    <div className="bg-dark-card rounded-lg p-5 border border-dark-border">
                      <div className="font-bold text-primary text-base mb-3">Estimation achat/revente</div>
                      <div className="space-y-2">
                        {acq.max_purchase_price != null && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted">Prix d'achat max</span>
                            <span className="text-sm font-bold text-primary">{formatPrice(acq.max_purchase_price)}</span>
                          </div>
                        )}
                        {resale.optimal_price != null && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted">Revente optimale</span>
                            <span className="text-sm font-bold text-emerald-400">{formatPrice(resale.optimal_price)}</span>
                          </div>
                        )}
                        {resale.estimated_profit != null && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted">Profit estime</span>
                            <span className="text-sm font-bold text-emerald-400">+{formatPrice(resale.estimated_profit)}</span>
                          </div>
                        )}
                        {resale.estimated_days_to_sell != null && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-muted">Delai de vente estime</span>
                            <span className="text-sm text-secondary">~{resale.estimated_days_to_sell}j</span>
                          </div>
                        )}
                        {ctx.active_competitors != null && (
                          <div className="flex justify-between items-center pt-1 border-t border-dark-border">
                            <span className="text-xs text-muted">Concurrents actifs</span>
                            <span className="text-sm text-secondary">{ctx.active_competitors}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* SECTION 7 — ANALYSE COMPORTEMENT CONCURRENTS LIVE */}
                {insights.live_behavior && liveCount > 0 && (
                  <div className="bg-dark-card rounded-lg p-5 border border-dark-border">
                    <div className="font-bold text-primary text-base mb-3">Comportement des concurrents</div>
                    <div className="text-xs text-muted mb-4">Analyse des {liveCount} annonces similaires en ligne</div>

                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-dark-bg rounded-lg p-3 text-center">
                        <div className="text-[11px] text-muted">Âge moyen</div>
                        <div className="text-lg font-bold text-primary">{insights.live_behavior.avg_days_online}j</div>
                      </div>
                      <div className="bg-dark-bg rounded-lg p-3 text-center">
                        <div className="text-[11px] text-muted">Ont baissé</div>
                        <div className="text-lg font-bold text-orange-400">{insights.live_behavior.pct_with_price_drop}%</div>
                      </div>
                      <div className="bg-dark-bg rounded-lg p-3 text-center">
                        <div className="text-[11px] text-muted">Baisse moy.</div>
                        <div className="text-lg font-bold text-yellow-400">{insights.live_behavior.avg_drop_pct}%</div>
                      </div>
                    </div>

                    {insights.live_behavior.turnover_trend && (
                      <div className="text-xs text-muted bg-dark-bg rounded-lg p-3">
                        {insights.live_behavior.turnover_trend === 'growing'
                          ? "📈 Le nombre d'annonces augmente sur ce segment — la concurrence s'intensifie."
                          : insights.live_behavior.turnover_trend === 'shrinking'
                            ? '📉 Le nombre d\'annonces diminue — les véhicules partent plus vite qu\'ils n\'arrivent.'
                            : "➡️ Le marché est stable sur ce segment — autant d'arrivées que de départs."}
                        {insights.live_behavior.new_listings_last_30d != null && (
                          <span className="block mt-1">
                            {insights.live_behavior.new_listings_last_30d} nouvelles annonces / {insights.live_behavior.removed_listings_last_30d} disparues ces 30 derniers jours.
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })() : (
            <div className="bg-dark-card rounded-lg p-8 text-center">
              <AlertCircle className="h-8 w-8 text-muted mx-auto mb-3" />
              <p className="text-muted">Aucune donnée marché disponible.</p>
            </div>
          )}
        </div>
      )}

      {/* Reserve modal */}
      {showReserveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-dark-card w-full sm:w-[400px] sm:rounded-2xl rounded-t-2xl">
            <div className="flex items-center justify-between p-5 border-b border-dark-border">
              <h2 className="text-lg font-bold text-primary">Réserver ce véhicule</h2>
              <button onClick={() => setShowReserveModal(false)} className="p-1 hover:bg-dark-hover rounded-full">
                <X className="h-5 w-5 text-muted" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-secondary">Vous pouvez noter pour qui ce véhicule est réservé (optionnel).</p>
              <div>
                <label className="block text-sm font-medium text-label mb-1">Nom / Société</label>
                <input
                  type="text"
                  value={reserveName}
                  onChange={(e) => setReserveName(e.target.value)}
                  placeholder="Ex: Garage Dupont"
                  className="w-full px-3 py-2.5 bg-dark-input border-2 border-dark-border rounded-xl text-sm text-primary placeholder-muted focus:border-carmin focus:ring-0"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-label mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={reservePhone}
                  onChange={(e) => setReservePhone(e.target.value)}
                  placeholder="Ex: +32 470 123 456"
                  className="w-full px-3 py-2.5 bg-dark-input border-2 border-dark-border rounded-xl text-sm text-primary placeholder-muted focus:border-carmin focus:ring-0"
                />
              </div>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setShowReserveModal(false)}
                  className="flex-1 py-3 border border-dark-border text-label rounded-xl text-sm font-medium hover:bg-dark-hover"
                >
                  Annuler
                </button>
                <button
                  onClick={handleReserveSubmit}
                  disabled={changingStatus}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-carmin text-white rounded-xl text-sm font-semibold hover:bg-carmin-light disabled:opacity-50"
                >
                  {changingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />}
                  Réserver
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;
